import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchOnBoarding, fetchOnBoardingInfo, fetchOnBoardingQuestion, fetchOnBoardingReviews } from './thunk';
import {
  OnBoarding,
  OnBoardingInfo,
  OnBoardingMap,
  OnBoardingQuestion,
  OnBoardingState,
  OnBoardingStepType,
  OnBoardingVars,
} from './types';

const initialState: OnBoardingState = {
  id: null,
  questions: [],
  infos: [],
  reviews: {
    limit: 15,
    offset: 0,
    isDisabled: false,
    data: [],
  },
  map_data: [],
  vars: { clients_total: '' },
  available_vars: [],
  steps: [],
  selectedAnswers: {},
  currentStep: null,
  isLoading: true,
  main_page: '',
  name: '',
  button_label: '',
  show_additional_button: false,
  additional_button_label: '',
  register_user_id: null,
  redirect_token: null,
  show_mindly_logo: true,
  show_close_button: false,
  onboarding_key: null,
  lang: '',
};

export const onBoardingSlice = createSlice({
  name: 'onBoarding',
  initialState,
  reducers: {
    answerOnClick: (
      state,
      action: PayloadAction<{
        answerId: string;
        questionId: string;
      }>
    ) => {
      const question = state.questions.find((q) => q.id === action.payload.questionId);
      if (!question?.id) return;

      if (!question.is_multiple) {
        state.selectedAnswers[question.id] = [action.payload.answerId];
        return;
      }

      const answers = state.selectedAnswers?.[question.id];

      if (!answers) {
        state.selectedAnswers[question.id] = [];
      }

      const answer = answers?.find((a) => a === action.payload.answerId);

      if (answer) {
        state.selectedAnswers[question.id] = answers.filter((a) => a !== action.payload.answerId) ?? [];
      } else {
        state.selectedAnswers[question.id].push(action.payload.answerId);
      }
    },
    addSteps: (
      state,
      action: PayloadAction<{
        nextSteps: OnBoardingStepType[];
        currentStep: OnBoardingStepType;
      }>
    ) => {
      const { nextSteps, currentStep } = action.payload;
      const index = state.steps.findIndex((s) => s.id === currentStep.id && s.type === currentStep.type);

      const newSteps = nextSteps.filter((s) => !state.steps.find((f) => f.id === s.id && f.type === s.type));

      if (newSteps.length) {
        state.steps.splice(index + 1, 0, ...newSteps);
      }
    },
    removeSteps: (state, action: PayloadAction<OnBoardingStepType[]>) => {
      return {
        ...state,
        steps: state.steps.filter((s) => !action.payload.find((f) => f.id === s.id && f.type === s.type)),
      };
    },
    setCurrentStep: (state, action: PayloadAction<OnBoardingMap | null>) => {
      state.currentStep = action.payload;
    },
    addVars: (state, action: PayloadAction<OnBoardingVars>) => {
      state.vars = { ...state.vars, ...action.payload };
    },
    removeVars: (
      state,
      action: PayloadAction<
        {
          name: string;
          value: string;
        }[]
      >
    ) => {
      action.payload.forEach((v) => {
        delete state.vars[v.name];
      });
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setOnBoardingId: (state, action: PayloadAction<string>) => {
      state.id = action.payload;
    },
    setRegisterUserID: (state, action: PayloadAction<string>) => {
      state.register_user_id = action.payload;
    },
    setRedirectToken: (state, action: PayloadAction<string>) => {
      state.redirect_token = action.payload;
    },
    resetOnBoarding: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOnBoarding.fulfilled, (state, action: PayloadAction<OnBoarding>) => {
      state.map_data = action.payload.map_data;
      state.vars = action.payload.vars;
      state.available_vars = action.payload.available_vars;
      state.answer_conditions = action.payload?.answer_conditions ?? undefined;
      state.main_page = action.payload.main_page;
      state.button_label = action.payload.button_label;
      state.show_additional_button = action.payload.show_additional_button;
      state.additional_button_label = action.payload.additional_button_label;
      state.name = action.payload.name;
      state.currentStep = null;
      state.steps = action.payload.map_data.map((s) => ({
        type: s.type,
        id: s.id,
      }));
      state.show_mindly_logo = action.payload.show_mindly_logo;
      state.show_close_button = action.payload.show_close_button;
      state.onboarding_key = action?.payload?.onboarding_key ?? null;
      state.lang = action?.payload?.lang ?? '';
    });
    builder.addCase(fetchOnBoardingQuestion.fulfilled, (state, action: PayloadAction<OnBoardingQuestion>) => {
      state.questions.push(action.payload);
      state.isLoading = false;
    });
    builder.addCase(fetchOnBoardingQuestion.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchOnBoardingInfo.fulfilled, (state, action: PayloadAction<OnBoardingInfo>) => {
      state.infos.push(action.payload);
      state.isLoading = false;
    });
    builder.addCase(fetchOnBoardingInfo.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchOnBoardingReviews.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchOnBoardingReviews.fulfilled, (state, action) => {
      state.reviews.offset += action.payload.length;
      state.reviews.isDisabled = action.payload.length < state.reviews.limit;
      state.reviews.data = [...state.reviews.data, ...action.payload];
      state.isLoading = false;
    });
  },
});

export default onBoardingSlice.reducer;
export const {
  answerOnClick,
  addSteps,
  removeSteps,
  setCurrentStep,
  addVars,
  setIsLoading,
  setOnBoardingId,
  removeVars,
  setRegisterUserID,
  setRedirectToken,
  resetOnBoarding,
} = onBoardingSlice.actions;
